/**
 * Company: SPHMedia
 * Description: [Restaurant block on article detail page]
 */

import LocationIcon from "@assets/restaurant/location.svg";
import {
  ADDRESS_HEADER_COPY,
  KAKI_MAKAN_EVENT_CATEGORY,
  OPERATING_HOURS_COPY,
} from "@config/kakiMakan";
import ApplyParagraphAnnotations from "@elements/ArticleParagraph/ApplyParagraphAnnotations";
import { InsertLineBreaks } from "@elements/ArticleParagraph/ParagraphElements/InsertLineBreaks";
import Link from "@elements/Typography/Link";
import Paragraph from "@elements/Typography/Paragraph";
import {
  CD_LANGUAGE,
  useBHCustomEventAnalyticsCall,
} from "@hooks/useAnalyticsCall/useBHCustomEventAnalyticsCall";
import { Heading } from "@sphtech/sph-web-ui/elements/Heading";
import { List, ListItem } from "@sphtech/sph-web-ui/elements/List";
import { ProcessedRestuarantContent } from "@typings/Opensearch";
import { ReactElement } from "react";

type RestaurantBlockProps = {
  location: ProcessedRestuarantContent;
  googleMapLink?: string;
};

/**
 * RestaurantBlock
 * Description: [Renders restaurant details in article page]
 * @param props - The props required by the component (if any)
 * @returns The rendered React element
 */
export default function RestaurantBlock({
  location,
  googleMapLink = "",
}: RestaurantBlockProps): ReactElement {
  const { title, address, openingHours } = location;
  const bhCustomEvent = useBHCustomEventAnalyticsCall();

  return (
    <>
      <div
        data-testid="location-info"
        className="flex flex-col gap-4 p-4 justify-start items-stretch bg-blue-250 text-neutral-1000"
      >
        <Heading
          testId="location-title"
          Element="h5"
          className="text-md font-secondary font-medium leading-[120%]"
        >
          {title}
        </Heading>
        {address && (
          <span
            data-testid="location-address"
            className="text-sm font-semimedium leading-[150%]"
          >
            <strong>{ADDRESS_HEADER_COPY}: </strong>
            {address}
          </span>
        )}
        {openingHours.length > 0 && (
          <div className="text-sm font-semimedium leading-[150%]">
            <span>
              <strong>{OPERATING_HOURS_COPY}:</strong>
            </span>
            <List testId="location-opening-hrs" className="list-disc pl-5">
              {openingHours.map((para, index) => (
                <ListItem
                  testId={`location-time-${index}`}
                  key={`list-item-${index}`}
                >
                  <Paragraph
                    testid={`paragraph-${index}`}
                    className="text-sm font-semimedium leading-[150%] text-neutral-1000 m-0"
                    id={para.id}
                  >
                    <InsertLineBreaks
                      content={ApplyParagraphAnnotations(
                        para.value,
                        para.annotations,
                      )}
                    />
                  </Paragraph>
                </ListItem>
              ))}
            </List>
          </div>
        )}
        {googleMapLink && (
          <Link
            relativePath={false}
            destination={googleMapLink}
            target="_blank"
            testId="google-link"
            handleClick={() => {
              bhCustomEvent(
                KAKI_MAKAN_EVENT_CATEGORY,
                "click",
                "get there",
                undefined,
                CD_LANGUAGE.ENGLISH,
              );
            }}
            className="w-[120px] h-[40px] border border-solid border-brand-primary flex justify-center items-center gap-0.5 py-6"
          >
            <span className="text-xs text-brand-primary leading-[120%] font-regular">
              Get there
            </span>
            <img src={LocationIcon} alt="location-icon" width={20} />
          </Link>
        )}
      </div>
    </>
  );
}
